/* Native Shopify section. Prices and date checks always come from the Dashboard. */
(() => {
  if (customElements.get('hob-event-inquiry')) return;
  const STEPS = ['Experience', 'Time & place', 'Your choices', 'Estimate', 'Contact'];
  const money = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  const element = (tag, text, className) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  };

  class EventInquiry extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.generation = (this.generation || 0) + 1;
      this.step = 0;
      this.revision = 0;
      this.abort = new AbortController();
      this.form = this.querySelector('form');
      this.app = this.querySelector('[data-app]');
      this.resetTransientState();
      this.base = this.dataset.proxyBase || '/apps/route/events';
      this.key = this.dataset.draftKey || 'hob-event-inquiry-v1';
      this.requestKey = crypto.randomUUID();
      // Keep requests on the storefront origin; this is never a secret-bearing URL.
      if (!/^\/apps\/[a-zA-Z0-9/_-]+$/.test(this.base) || this.dataset.enabled !== 'true') return;
      this.form.addEventListener('submit', (event) => { event.preventDefault(); void this.advance(); }, { signal: this.abort.signal });
      this.form.addEventListener('input', () => this.changed(), { signal: this.abort.signal });
      this.addEventListener('click', (event) => {
        const target = event.target.closest('button');
        if (!target) return;
        if (target.hasAttribute('data-back')) this.showStep(this.step - 1);
        if (target.hasAttribute('data-edit')) this.showStep(Number(target.dataset.edit));
        if (target.hasAttribute('data-reset')) this.resetDraft();
      }, { signal: this.abort.signal });
      void this.initialize();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resetTransientState();
      this.initialized = false;
    }

    resetTransientState() {
      this.busy = false;
      if (this.form) this.form.inert = false;
      const reset = this.querySelector('[data-reset]');
      if (reset) reset.disabled = false;
      const status = this.querySelector('[data-status]');
      if (status) status.textContent = '';
    }

    async request(path, body) {
      const controller = new AbortController();
      const lifetimeSignal = this.abort.signal;
      const stop = () => controller.abort();
      lifetimeSignal.addEventListener('abort', stop, { once: true });
      const timer = setTimeout(stop, 20000);
      try {
        let response;
        try {
          response = await fetch(`${this.base}/${path}`, {
            method: body ? 'POST' : 'GET',
            headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
            ...(body ? { body: JSON.stringify(body) } : {}),
            cache: 'no-store', credentials: 'same-origin', signal: controller.signal,
          });
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          throw new Error('We couldn’t reach our event planner. Please try again or contact our team.');
        }
        let data;
        try { data = await response.json(); } catch { throw new Error('We couldn’t reach our event planner. Please try again or contact our team.'); }
        if (!response.ok || !data.ok) {
          const message = response.status === 429 ? 'Please wait a moment before trying again.' : data.error;
          throw new Error(message || 'We couldn’t complete that step. Please try again.');
        }
        return data;
      } catch (error) {
        if (error.name === 'AbortError') throw new Error('This is taking longer than expected. Your draft is saved; please try again.');
        throw error;
      } finally {
        clearTimeout(timer);
        lifetimeSignal.removeEventListener('abort', stop);
      }
    }

    async initialize() {
      const generation = this.generation;
      try {
        this.config = await this.request('config');
        if (!this.isConnected || generation !== this.generation) return;
        if (!Array.isArray(this.config.services) || !this.config.services.length) return;
        this.querySelector('[data-services]').replaceChildren(...this.config.services.map((service) => this.choice('service', service.id, service.label, 'radio')));
        this.querySelector('[data-timezone]').textContent = `All times are in ${this.config.timezone || 'America/New_York'}. We’ll plan arrival and setup around your service time.`;
        this.restoreDraft();
        this.renderChoices();
        this.restoreChoices();
        this.querySelector('[data-fallback]').hidden = true;
        this.app.hidden = false;
        this.showStep(this.pendingSubmission ? 4 : 0, false);
      } catch (error) {
        if (!this.isConnected || generation !== this.generation) return;
        // The always-present contact link remains usable during outages or before launch.
        this.querySelector('[data-fallback]').prepend(element('span', `${error.message} `));
      }
    }

    field(name) { return this.form.elements.namedItem(name); }
    value(name) { return String(this.field(name)?.value || '').trim(); }
    checked(name) { return Array.from(this.form.querySelectorAll(`input[name="${name}"]:checked`)).map((input) => input.value); }
    selection() {
      return {
        service: this.value('service'), eventName: this.value('eventName'), occasion: this.value('occasion'),
        date: this.value('date'), startTime: this.value('startTime'), durationMinutes: Number(this.value('durationMinutes')),
        timezone: this.config.timezone || 'America/New_York', venue: this.value('venue'), city: this.value('city'),
        postalCode: this.value('postalCode'), guestCount: Number(this.value('guestCount')), travelZone: this.value('travelZone'),
        addons: this.checked('addons'), flavorIds: this.checked('flavorIds'),
        products: Array.from(this.querySelectorAll('[data-product-id]')).filter((input) => Number(input.value) > 0).map((input) => ({ id: input.dataset.productId, quantity: Number(input.value) })),
        notes: this.value('notes'), logistics: {
          onsiteContact: this.value('onsiteContact'), dietaryNotes: this.value('dietaryNotes'),
          ...(this.value('powerAvailable') ? { powerAvailable: this.value('powerAvailable') === 'yes' } : {}),
          ...(this.value('tableProvided') ? { tableProvided: this.value('tableProvided') === 'yes' } : {}),
        },
      };
    }

    choice(name, value, label, type = 'checkbox') {
      const wrapper = element('label', undefined, type === 'radio' || name === 'addons' ? 'hob-event__choice' : 'hob-event__check');
      const input = document.createElement('input');
      input.type = type; input.name = name; input.value = value;
      if (type === 'radio') input.required = true;
      wrapper.append(input, element('span', label));
      return wrapper;
    }

    renderChoices() {
      const service = this.value('service');
      const addons = (this.config.addons || []).filter((item) => item.services.includes(service));
      this.querySelector('[data-addons]').replaceChildren(...addons.map((item) => this.choice('addons', item.id, item.label)));
      this.querySelector('[data-addons-wrap]').hidden = !addons.length;
      const products = (this.config.products || []).filter((item) => item.services.includes(service));
      this.querySelector('[data-products]').replaceChildren(...products.map((item) => {
        const wrapper = element('label', undefined, 'hob-event__product');
        const title = element('span', item.label);
        const servings = item.servingsPerUnit > 0 ? ` · serves about ${item.servingsPerUnit}` : '';
        title.append(element('small', `${item.unitLabel || 'Each'}${servings}`));
        const input = document.createElement('input');
        Object.assign(input, { type: 'number', min: '0', max: '10000', step: '1', value: '0', inputMode: 'numeric' });
        input.dataset.productId = item.id; input.dataset.servings = String(item.servingsPerUnit || 0);
        input.setAttribute('aria-label', `${item.label} quantity`);
        wrapper.append(title, input);
        return wrapper;
      }));
      this.querySelector('[data-products-wrap]').hidden = !products.length;
      const flavors = this.config.flavors || [];
      this.querySelector('[data-flavors]').replaceChildren(...flavors.map((item) => this.choice('flavorIds', item.id, item.label)));
      this.querySelector('[data-flavors-wrap]').hidden = service !== 'station' || !flavors.length;
      this.querySelectorAll('[data-station-field]').forEach((field) => { field.hidden = service !== 'station'; });
      this.renderedService = service;
    }

    changed() {
      this.revision += 1;
      this.requestKey = crypto.randomUUID();
      this.pendingSubmission = null;
      this.estimate = null;
      if (this.value('service') !== this.renderedService) this.renderChoices();
      this.error('');
      this.saveDraft();
    }

    saveDraft() {
      const fields = {};
      for (const input of this.form.elements) {
        if (input.name && !['addons', 'flavorIds', 'website'].includes(input.name)) {
          if (input.type === 'radio' && !input.checked) continue;
          fields[input.name] = input.type === 'checkbox' ? input.checked : input.value;
        }
      }
      try {
        localStorage.setItem(this.key, JSON.stringify({ fields, selection: this.selection(), requestKey: this.requestKey, pendingSubmission: this.pendingSubmission, savedAt: Date.now() }));
      } catch { /* Privacy mode can disable local storage; form submission still works. */ }
    }

    restoreDraft() {
      try {
        const draft = JSON.parse(localStorage.getItem(this.key) || 'null');
        if (!draft || Date.now() - draft.savedAt > 7 * 86400000) { localStorage.removeItem(this.key); return; }
        this.restored = draft;
        for (const [name, value] of Object.entries(draft.fields || {})) {
          const input = this.field(name);
          if (!input || name === 'website') continue;
          if (input.type === 'checkbox') input.checked = value === true;
          else input.value = String(value);
        }
        if (typeof draft.requestKey === 'string') this.requestKey = draft.requestKey;
        if (draft.pendingSubmission?.idempotencyKey === this.requestKey) this.pendingSubmission = draft.pendingSubmission;
        this.querySelector('[data-saved]').hidden = false;
      } catch { /* Ignore invalid or inaccessible browser drafts. */ }
    }

    restoreChoices() {
      const selection = this.restored?.selection;
      if (!selection) return;
      for (const name of ['addons', 'flavorIds']) {
        this.form.querySelectorAll(`input[name="${name}"]`).forEach((input) => { input.checked = (selection[name] || []).includes(input.value); });
      }
      this.querySelectorAll('[data-product-id]').forEach((input) => {
        input.value = String((selection.products || []).find((p) => p.id === input.dataset.productId)?.quantity || 0);
      });
    }

    resetDraft() {
      this.form.reset();
      this.restored = null;
      this.renderChoices();
      this.changed();
      try { localStorage.removeItem(this.key); } catch { /* Optional draft storage. */ }
      this.querySelector('[data-saved]').hidden = true;
      this.querySelector('[data-availability]').hidden = true;
      this.showStep(0);
    }

    validateStep(step) {
      const fieldset = this.querySelector(`[data-step="${step}"]`);
      for (const input of fieldset.querySelectorAll('input, select, textarea')) {
        if (input.closest('[hidden]')) continue;
        if (!input.checkValidity()) { input.reportValidity(); return false; }
      }
      if (step === 0 && !this.value('service')) { this.error('Choose an experience to continue.'); return false; }
      if (step === 2 && this.checked('flavorIds').length > 6) { this.error('Please choose up to six babka flavors.'); return false; }
      return true;
    }

    showStep(step, focus = true) {
      this.step = Math.max(0, Math.min(4, step));
      this.querySelectorAll('[data-step]').forEach((fieldset) => { fieldset.hidden = Number(fieldset.dataset.step) !== this.step; });
      this.querySelector('[data-progress]').textContent = `Step ${this.step + 1} of 5 · ${STEPS[this.step]}`;
      this.querySelector('[data-back]').hidden = this.step === 0;
      this.querySelector('[data-next]').textContent = this.step === 4 ? (this.field('waitlist').checked ? 'Join the waitlist' : 'Send event request') : 'Continue';
      this.error('');
      if (focus) this.querySelector(`[data-step="${this.step}"] legend`).focus({ preventScroll: false });
    }

    error(message) {
      const node = this.querySelector('[data-error]');
      node.textContent = message; node.hidden = !message;
    }

    async checkAvailability() {
      const revision = this.revision;
      const { availability } = await this.request('availability', { selection: this.selection(), priceBookVersionId: this.config.priceBookVersionId });
      if (revision !== this.revision) return false;
      const panel = this.querySelector('[data-availability]');
      panel.hidden = false;
      this.querySelector('[data-availability-message]').textContent = availability.message;
      const waitlist = this.querySelector('[data-waitlist-label]');
      waitlist.hidden = availability.status === 'available';
      if (availability.status === 'available') this.field('waitlist').checked = false;
      const alternatives = this.querySelector('[data-alternatives]');
      alternatives.replaceChildren(...(availability.alternatives || []).map((date) => {
        const button = element('button', new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }), 'button-secondary');
        button.type = 'button';
        button.addEventListener('click', () => {
          this.field('date').value = date;
          this.field('waitlist').checked = false;
          panel.hidden = true; this.changed(); this.showStep(1);
        });
        return button;
      }));
      return availability.status === 'available' || this.field('waitlist').checked;
    }

    suggestProducts() {
      const inputs = Array.from(this.querySelectorAll('[data-product-id]'));
      // Split the guest count across offered products, rather than suggesting a full event of each.
      if (inputs.some((input) => Number(input.value) > 0) || !inputs.length) return;
      const guests = Number(this.value('guestCount'));
      inputs.forEach((input) => { if (Number(input.dataset.servings) > 0) input.value = String(Math.ceil(guests / inputs.length / Number(input.dataset.servings))); });
      this.saveDraft();
    }

    async loadEstimate() {
      const revision = this.revision;
      const { estimate } = await this.request('estimate', { selection: this.selection(), priceBookVersionId: this.config.priceBookVersionId });
      if (revision !== this.revision) return false;
      this.estimate = estimate;
      const selection = this.selection();
      const label = this.config.services.find((item) => item.id === selection.service)?.label || selection.service;
      const itemLabels = (items) => new Map((items || []).map((item) => [item.id, item.label]));
      const productLabels = itemLabels(this.config.products);
      const addonLabels = itemLabels(this.config.addons);
      const flavorLabels = itemLabels(this.config.flavors);
      const selectedLabel = (name) => this.field(name)?.selectedOptions?.[0]?.textContent.trim() || this.value(name);
      const summary = [
        element('p', `${label} · ${selection.guestCount} guests`),
        ...(selection.eventName ? [element('p', `Event: ${selection.eventName}${selection.occasion ? ` · ${selection.occasion}` : ''}`)] : []),
        element('p', `${selection.date} at ${selection.startTime} · ${selection.durationMinutes / 60} hours`),
        element('p', `${selection.venue}, ${selection.city} ${selection.postalCode}`),
        ...(selection.travelZone ? [element('p', `Travel area: ${selectedLabel('travelZone')}`)] : []),
        ...(selection.products.length ? [element('p', `Products: ${selection.products.map((product) => `${productLabels.get(product.id) || product.id} × ${product.quantity}`).join(', ')}`)] : []),
        ...(selection.addons.length ? [element('p', `Packages and extras: ${selection.addons.map((id) => addonLabels.get(id) || id).join(', ')}`)] : []),
        ...(selection.flavorIds.length ? [element('p', `Babka flavors: ${selection.flavorIds.map((id) => flavorLabels.get(id) || id).join(', ')}`)] : []),
        ...(selection.logistics.onsiteContact ? [element('p', `Contact at the event: ${selection.logistics.onsiteContact}`)] : []),
        ...('powerAvailable' in selection.logistics ? [element('p', `Power available: ${selectedLabel('powerAvailable')}`)] : []),
        ...('tableProvided' in selection.logistics ? [element('p', `Table at the venue: ${selectedLabel('tableProvided')}`)] : []),
        ...(selection.logistics.dietaryNotes ? [element('p', `Allergies / dietary requirements: ${selection.logistics.dietaryNotes}`)] : []),
        ...(selection.notes ? [element('p', `Notes: ${selection.notes}`)] : []),
      ];
      this.querySelector('[data-summary]').replaceChildren(...summary);
      const panel = this.querySelector('[data-estimate]');
      panel.replaceChildren();
      if (this.field('waitlist').checked || estimate.mode === 'review') {
        panel.append(element('p', this.field('waitlist').checked ? 'Waitlist request — our team will review the date before pricing your event.' : estimate.message, 'hob-event__total'));
      } else {
        const list = document.createElement('dl');
        for (const line of estimate.lines || []) list.append(element('dt', `${line.label}${line.quantity > 1 ? ` × ${line.quantity}` : ''}`), element('dd', money(line.totalCents)));
        panel.append(list, element('p', estimate.mode === 'range' ? `${money(estimate.minCents)}–${money(estimate.maxCents)} estimated` : `${money(estimate.minCents)} estimated`, 'hob-event__total'));
        if (estimate.message) panel.append(element('p', estimate.message));
      }
      return true;
    }

    async advance() {
      if (this.busy || !this.validateStep(this.step)) return;
      const generation = this.generation;
      this.busy = true;
      this.form.inert = true;
      this.querySelector('[data-reset]').disabled = true;
      this.querySelector('[data-status]').textContent = this.step === 4 ? 'Sending your request…' : 'Checking your details…';
      this.error('');
      try {
        if (this.step === 1) {
          if (!(await this.checkAvailability())) { this.error('Choose an alternative date, or join the waitlist for this date.'); return; }
          this.suggestProducts();
        }
        if (this.step === 2 && !(await this.loadEstimate())) return;
        if (this.step === 4) {
          // A lost response may already have created an inquiry. Retry that exact
          // payload/key, even after reload or availability/price-book changes.
          if (!this.pendingSubmission) {
            if (!(await this.checkAvailability())) { this.showStep(1); this.error('Availability has changed. Please choose another date or join the waitlist.'); return; }
            this.pendingSubmission = {
              selection: this.selection(), priceBookVersionId: this.config.priceBookVersionId,
              contactName: this.value('contactName'), email: this.value('email'), phone: this.value('phone'), company: this.value('company'),
              waitlist: this.field('waitlist').checked, website: this.value('website'), idempotencyKey: this.requestKey,
            };
            this.saveDraft();
          }
          const result = await this.request('inquiries', this.pendingSubmission);
          if (!this.isConnected || generation !== this.generation) return;
          this.pendingSubmission = null;
          try { localStorage.removeItem(this.key); } catch { /* Submission has succeeded regardless. */ }
          this.app.hidden = true;
          const success = this.querySelector('[data-success]');
          success.replaceChildren(element('h3', 'Your request is with us.'), element('p', `Reference: ${result.reference}`), element('p', 'Our team will review the details and get in touch. Your date is not booked until we confirm it with you.'));
          success.hidden = false; success.focus();
          return;
        }
        this.showStep(this.step + 1, false);
        this.saveDraft();
      } catch (error) {
        if (!this.isConnected || generation !== this.generation) return;
        this.error(error.message || 'Something went wrong. Your draft is saved; please try again.');
      } finally {
        if (generation !== this.generation) return;
        this.busy = false; this.form.inert = false;
        this.querySelector('[data-reset]').disabled = false;
        this.querySelector('[data-status]').textContent = '';
        if (!this.app.hidden && this.querySelector('[data-error]').hidden) this.querySelector(`[data-step="${this.step}"] legend`).focus();
      }
    }
  }
  customElements.define('hob-event-inquiry', EventInquiry);
})();
